import React, {
  useContext,
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useMemo,
  useRef,
} from 'react';

import { toast } from 'react-toastify';

import fightApi from '../services/fightApi';

import plurals from '../utils/plurals';
import {
  belongsToFight,
  belongsToFightStrict,
  isUsableLegacyId,
} from '../utils/legacyId';

import {
  initData,
  FightData,
  IIndexable,
  ReducerAction,
  getFightersOutcome,
} from '../models/fight';
import fightApiV2 from '../services/fightApiV2';
import { AxiosError } from 'axios';
import apiV2 from '../services/api-mike-v2';

interface FightContextData {
  fight: FightData;
  dispatchFight: React.Dispatch<ReducerAction>;
  /** Recarrega a luta e o outcome da API (mesmo request do carregamento inicial). */
  fetchFight(): Promise<void>;
  updateFight(updateData: AtLeastOne<FightData>): void;

  getFight(): FightData;
  getRoundTimeInSeconds(): number;
  translateRoundTime(totalSeconds: number): string;
  translateCurrentRoundTime(): string;
}

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
  U[keyof U];

const FightContext = createContext<FightContextData>({} as FightContextData);

/** `reset` e `remove` destroem estado, então usam o guard fail-closed. */
const destructiveTypes = ['reset', 'remove'];

const fightReducer = (state: FightData, action: ReducerAction): FightData => {
  // Backstop: os handlers do socket já filtram, mas o reducer também é
  // alcançado por dispatches locais e por qualquer caminho novo.
  const guard = destructiveTypes.includes(action.type)
    ? belongsToFightStrict
    : belongsToFight;

  if (!guard(action.payload, state)) return { ...state };

  const newState = state;
  // O UUID primeiro: com lutadores sem legacyId, `fighterId` é `null` dos dois
  // lados e o `find` casava sempre o mesmo slot — toda ação contava para ambos.
  const outcome = newState.fightersOutcome?.find((authorOutcome) =>
    action.payload?.authorNewId && authorOutcome.fighterNewId
      ? authorOutcome.fighterNewId === action.payload.authorNewId
      : isUsableLegacyId(action.payload?.authorId) &&
        authorOutcome.fighterId === action.payload?.authorId,
  ) as IIndexable;

  const startActions = ['roundStart', 'timeout_end'];
  const stopActions = ['fightEnd', 'timeout_start'];
  switch (action.type) {
    case 'fetch':
      if(action.init?.fightersOutcome.length === 0) {
        // Sem `|| 1`, e com os UUIDs: o fallback dava o MESMO `fighterId: 1`
        // aos dois cantos, e os dois slots viravam o mesmo lutador.
        action.init.fightersOutcome = [
          getFightersOutcome(
            action.init?.legacyRedCornerId ?? null,
            action.init?.redCornerId,
          ),
          getFightersOutcome(
            action.init?.legacyBlueCornerId ?? null,
            action.init?.blueCornerId,
          ),
        ];
      }
      return { ...state, ...action.init };
    case 'time':
      return {
        ...state,
        currentMinutes: action.time?.currentMinutes,
        currentSeconds: action.time?.currentSeconds,
      };
    case 'add':
      // Entrega duplicada do mesmo `recorded` (reconexão, socket órfão) somaria
      // o stat duas vezes — a lista de actions deduplica por `_id`, aqui não
      // deduplicava.
      if (
        action.payload?._id &&
        newState.appliedActionIds?.includes(action.payload._id)
      )
        return { ...newState };

      if (action.payload && outcome)
        try {
          if (action.payload.name === 'score') {
            outcome[action.payload?.stance].score = action.payload.value;
            outcome.score = action.payload.value;
          } else if (action.payload.value) {
            outcome[action.payload?.stance].elapsedControlTime +=
              action.payload.value;
          } else {
            outcome[action.payload?.stance][plurals[action.payload.name]] += 1;
          }
          if (
            startActions.includes(action.payload.name) &&
            (newState.roundStatus === 'paused' || !newState.roundStatus)
          ) {
            newState.roundStatus = 'started';
          } else if (
            stopActions.includes(action.payload.name) &&
            newState.roundStatus === 'started'
          ) {
            newState.roundStatus = 'paused';
          }
          if (action.payload.name === 'roundEnd') {
            newState.currentRound = action.payload.round + 1;
            newState.totalRound = action.payload.round + 1;
            newState.currentMinutes = 0;
            newState.currentSeconds = 0;
            newState.roundStatus = undefined;
            newState.currentDomain = null;
            newState.currentStance = 'standing';
          }
        } catch (error) {
          return newState;
        }

      if (action.payload?._id) {
        // Janela suficiente para cobrir reentregas; sem crescer sem limite
        // numa luta longa.
        newState.appliedActionIds = [
          ...(newState.appliedActionIds || []),
          action.payload._id,
        ].slice(-200);
      }

      return { ...newState };
    case 'update':
      return { ...newState, ...action.updateData };
    case 'remove':
      if (action.payload && outcome)
        try {
          if (action.payload?.name === 'score' && action.payload?.value) {
            // Necessário verificar o tratamento do score ao revogar uma ação
            outcome[action.payload?.stance][plurals[action.payload.name]] -=
              action.payload?.value;
            outcome[plurals[action.payload.name]] -= action.payload?.value;
          } else {
            outcome[action.payload?.stance][plurals[action.payload.name]] -= 1;
          }
        } catch (error) {
          return newState;
        }

      if (action.payload?._id) {
        newState.appliedActionIds = (newState.appliedActionIds || []).filter(
          (id) => id !== action.payload?._id,
        );
      }

      return { ...newState };
    case 'reset':
      // eslint-disable-next-line no-case-declarations
      const initDataClone = JSON.parse(JSON.stringify(initData));
      // eslint-disable-next-line no-case-declarations
      const reset: FightData = {
        ...initDataClone,
        id: state.id,
        legacyId: state.legacyId,
        eventId: state.eventId,
        legacyEventId: state.legacyEventId,
        eventName: state.eventName,
        redCornerId: state.redCornerId,
        legacyRedCornerId: state.legacyRedCornerId,
        redCornerName: state.redCornerName,
        blueCornerId: state.blueCornerId,
        legacyBlueCornerId: state.legacyBlueCornerId,
        blueCornerName: state.blueCornerName,
        totalRound: state.totalRound,
        fightType: state.fightType,
        oneFightId: state.oneFightId,
        oneEventId: state.oneEventId,
        promotionId: state.promotionId,
      };
      reset.fightersOutcome[0].fighterId = state.legacyRedCornerId ?? null;
      reset.fightersOutcome[0].fighterNewId = state.redCornerId;
      reset.fightersOutcome[1].fighterId = state.legacyBlueCornerId ?? null;
      reset.fightersOutcome[1].fighterNewId = state.blueCornerId;

      return { ...reset };
    default:
      return newState;
  }
};

const FightProvider: React.FC = ({ children }) => {
  const memoReducer = useCallback(fightReducer, []);
  const [fight, dispatchFight] = useReducer(memoReducer, {
    ...initData,
    loading: true,
  });

  /** Espelho síncrono de `fight`. Ver `getFight` no contexto. */
  const fightRef = useRef(fight);
  fightRef.current = fight;

  const getFight = useCallback(() => fightRef.current, []);

  const fetchLegacyElements = useCallback(async (
    legacyId: number,
    eventLegacyId: number,
  ) => {
    try {
      const fightResponseV1 = await fightApi.get(
        `/fight/${legacyId}`,
      );

      const eventResponseV1 = await fightApi.get(
        `/events/${eventLegacyId}`,
      );

      return { fightResponseV1, eventResponseV1 };
    } catch (err) {
      return { fightResponseV1: undefined, eventResponseV1: undefined };
    }
  }, [] );

  const fetchFight = useCallback(async () => {
    if (fight.id === '') return;

    try {
      const fightResponse = await fightApiV2.get(`/fights/${fight.id}`);

      // Luta sem mapeamento legado é tolerada de propósito: as ações são
      // gravadas e correlacionadas pelo UUID (`fightNewId`). O warn fica só
      // para diagnóstico.
      if (!isUsableLegacyId(fightResponse.data.legacyId)) {
        console.warn(
          `Fight ${fight.id} sem legacyId utilizável (${fightResponse.data.legacyId}); usando apenas o UUID.`,
        );
      }
      // const { eventResponseV1, fightResponseV1 } = await fetchLegacyElements(
      //   fightResponse.data.legacyId,
      //   fightResponse.data.event.legacyId,
      // );
      const response = await apiV2.get(
        `/fights/${fightResponse.data.id}`,
      );
      const controlMode = fightResponse.data.sport !== 'WRESTLING';

      dispatchFight({
        type: 'fetch',
        init: {
          id: fight.id,
          legacyId: fightResponse.data.legacyId,
          loading: false,
          controlMode,
          // Sem `|| 1`: com os dois lutadores sem legacyId, o fallback dava o
          // MESMO `fighterId: 1` aos dois cantos e o outcome virava um só.
          fightersOutcome: [
            getFightersOutcome(
              fightResponse.data.redCorner.legacyId ?? null,
              fightResponse.data.redCornerId,
            ),
            getFightersOutcome(
              fightResponse.data.blueCorner.legacyId ?? null,
              fightResponse.data.blueCornerId,
            ),
          ],
          redCornerId: fightResponse.data.redCornerId,
          legacyRedCornerId: fightResponse.data.redCorner.legacyId,
          blueCornerId: fightResponse.data.blueCornerId,
          legacyBlueCornerId: fightResponse.data.blueCorner.legacyId,
          totalRound: fightResponse.data.totalRound,
          fightType: fightResponse.data.sport,
          eventId: fightResponse.data.eventId,
          promotionId: fightResponse.data.event.promotionId,
          legacyEventId: fightResponse.data.event.legacyId,
          eventName: fightResponse.data.event.name,
          redCornerName: `${fightResponse.data.redCorner.firstName} "${fightResponse.data.redCorner.nickname}" ${fightResponse.data.redCorner.lastName}`,
          blueCornerName: `${fightResponse.data.blueCorner.firstName} "${fightResponse.data.blueCorner.nickname}" ${fightResponse.data.blueCorner.lastName}`,
          oneFightId: fightResponse.data.oneFightId,// || fightResponseV1?.data.oneFightId,
          oneEventId: fightResponse.data.event.oneEventId,// ||
            // eventResponseV1?.data.oneEventId,
          ...response.data,
          status: response.data.status || 'inactive',
        },
      });
    } catch (error) {
      if ((error as AxiosError).request) {
        toast.error('Luta não encontrada na api V2, scout não disponível');
      } else {
        toast.error((error as Error).message);
      }
    }
  }, [fight.id, fetchLegacyElements, dispatchFight]);

  useEffect(() => {
    fetchFight();
  }, [fetchFight]);

  const updateFight = useCallback(
    (updateData: AtLeastOne<FightData>) => {

      fightRef.current = { ...fightRef.current, ...updateData } as FightData;
      dispatchFight({
        type: 'update',
        updateData: updateData as Partial<FightData>,
      });
    },
    [],
  );

  const getRoundTimeInSeconds = useCallback(() => {
    const minutes = fight.currentMinutes;
    const seconds = fight.currentSeconds;

    return (minutes || 0) * 60 + (seconds || 0);
  }, [fight]);

  const translateRoundTime = useCallback((totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds - minutes * 60;

    const aux = seconds < 10 ? '0' : '';

    return `${minutes}:${aux}${seconds}`;
  }, []);

  const translateCurrentRoundTime = useCallback(() => {
    const minutes = fight.currentMinutes;
    const seconds = fight.currentSeconds;

    const aux = (seconds || 0) < 10 ? '0' : '';

    return `${minutes || '00'}:${aux}${seconds}`;
  }, [fight]);

  const contextValue = useMemo(
    () => ({
      fight,
      dispatchFight,
      fetchFight,
      updateFight,
      getFight,
      getRoundTimeInSeconds,
      translateRoundTime,
      translateCurrentRoundTime,
    }),
    [fight, dispatchFight, fetchFight, updateFight, getFight, getRoundTimeInSeconds, translateRoundTime, translateCurrentRoundTime]
  );

  return (
    <FightContext.Provider
      value={contextValue}
    >
      {children}
    </FightContext.Provider>
  );
};

function useFight(): FightContextData {
  const context = useContext(FightContext);

  if (!context) {
    throw new Error('useFight must be used within FightProvider');
  }

  return context;
}

export { useFight, FightProvider, fightReducer };
